/**
@jsxRuntime
classic */
/** @jsx jsx */
import {jsx} from '@emotion/core'
import {Routes, Route, Navigate} from 'react-router-dom'
import * as Sentry from '@sentry/react'
// import {BusinessResults} from './screens/business-results'
// import {BusinessScan} from './screens/business-form'
// import {BusinessScans} from './screens/business-scans'
import {Appointments} from './screens/appointments'
import {AppointmentsRedesign} from './pages/AppointmentsRedesign'
// import {ProductShowcase} from './screens/product-showcase'
// import {MyAccounts} from './screens/my-accounts'
import {AllAccounts} from './accountManagement/features/AccountsTabs/AllAccounts'
import {MyAccounts} from './accountManagement/features/AccountsTabs/MyAccounts'
import {KeapSandbox} from './accountManagement/features/AccountsTabs/KeapSandbox'
import {ToolsPage, ToolsContent} from './screens/tools-page'
import {TOOL_SECTIONS} from './components/tools-page/tools-consts'
import {NewDemo} from './screens/new-demo'
import {HeaderTitle} from './components/header-title'
import {Orders} from './screens/orders'
import {PendingLinks} from './screens/pending-links'
import {CompletedLinks} from './screens/completed-links'
import NotFoundPage from './components/not-found-page'
import {NewOrder} from './screens/new-order'
import {UpgradeOrder} from './screens/upgrade-order'
import {NewOrderShortCut} from './screens/new-order-short-cut'
import {PackageRecommendation} from './screens/package-recommendation'
import {PackageResult} from './screens/package-result'
import {FeaturesTable} from './screens/feature-summary'
import {Success} from './screens/success'
import {Support} from './screens/support'
import Admin from './screens/admin'
import {SalesCollateral} from './screens/sales-collateral'
import {
  ErrorContainer,
  TextContainer,
  ImageContainer,
  ItemContainer,
  ScreenContainer,
} from './components/ui/error-fallback'
import {thryvGuyConfused} from './assets'
import {ParagraphText, Button} from '@thryvlabs/maverick'
import {useUserFeatures} from './context/user-features-context'
import {useUserRoles} from './utils/use-user-roles'
import {
  getAccountManagementLandingPath,
  isAccountPathHiddenForRoles,
} from './utils/account-management-navigation'
import {PriceCalculator} from './screens/price-calculator'
import {ControlCenterAdmin} from './screens/control-center-admin'
import CheckStatus from './screens/check-status'
import {Coupons} from './screens/coupons'
import {NewCoupon} from './screens/new-coupon'
import ProductGrpServiceIdManager from './screens/product-serviceID-manager'
import {Accounts} from './accountManagement/Accounts'
import {AccountSetupPage} from './accountManagement/features/AccountSetup'
import {BundleCouponReport} from './screens/bundle-coupon-report'
import {FeatureFlags} from './screens/features'
import {ProposalMain} from './pages/ProposalTool/ProposalMain'
import {ProposalToolDashboard} from './pages/ProposalTool/features/Dashboard/ProposalToolDashboard'
import {ProposalBuilder} from './pages/ProposalTool/features/ProposalBuilder'
import {ProposalMetrics} from './pages/ProposalTool/features/proposalMetrics/proposal-metrics'
import {PurchaseLinksDashboard} from './screens/purchase-links-dashboard'
import {PaymentErrorsDashboard} from './screens/payment-errors-dashboard'

function FullPageErrorFallback({error}) {
  const handleClick = () => window.location.reload()

  return (
    <ScreenContainer>
      <ErrorContainer>
        <ItemContainer>
          <TextContainer>
            <ParagraphText
              variant="reg"
              style={{
                color: '#ff5101',
                fontWeight: 'bold',
                fontSize: '2rem',
                marginBottom: '20px',
              }}
            >
              Uh Oh!
            </ParagraphText>
            <ParagraphText
              variant="reg"
              className="m-0"
              style={{fontWeight: 'bold', fontSize: '1rem'}}
            >
              There&apos;s a problem. Try refreshing the app.
            </ParagraphText>
          </TextContainer>
          <Button
            onClick={handleClick}
            variant="primary"
            style={{marginTop: '50px'}}
          >
            Refresh Now
          </Button>
        </ItemContainer>
        <ImageContainer>
          <img src={thryvGuyConfused} alt="confused Thryv Guy" />
        </ImageContainer>
      </ErrorContainer>
    </ScreenContainer>
  )
}

function AuthenticatedApp() {
  const featureFlags = useUserFeatures()
  const {
    // Business_Scan_Access,
    Orders_Menu_Access: hasOrdersLinkAccess,
    Coupon_Config_Access: hasCouponConfigAccess,
    Feature_Flags_Read_Access: hasFeatureFlagsReadAccess,
    proposal_metrics: hasProposalMetricsAccess,
    Purchase_Links_Dashboard_Access: hasPurchaseLinksDashboardAccess,
    Payment_Errors_Dashboard_Access: hasPaymentErrorsDashboardAccess,
    Keap_Sandbox_Access: hasKeapSandboxAccess,
  } = useUserFeatures()
  const {roles} = useUserRoles()
  const accountManagementLandingPath = getAccountManagementLandingPath(roles)
  const accountsIndexPath = isAccountPathHiddenForRoles(
    '/accounts/all-accounts',
    roles,
  )
    ? accountManagementLandingPath
    : '/accounts/all-accounts'

  return (
    <Sentry.ErrorBoundary fallback={FullPageErrorFallback}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={accountManagementLandingPath} replace />}
        />
        {/* Accounts Parent Route */}
        <Route path="/accounts" element={<Accounts />}>
          {/* Default /accounts route */}
          <Route index element={<Navigate to={accountsIndexPath} replace />} />

          {/* Child Routes */}
          <Route
            path="my-accounts"
            element={
              <HeaderTitle title="My Accounts">
                <MyAccounts />
              </HeaderTitle>
            }
          />
          <Route
            path="account-setup"
            element={
              <HeaderTitle title="All Accounts">
                <AccountSetupPage />
              </HeaderTitle>
            }
          />
          <Route
            path="all-accounts"
            element={
              <HeaderTitle title="All Accounts">
                <AllAccounts />
              </HeaderTitle>
            }
          />
          {hasKeapSandboxAccess && (
            <Route
              path="keap-sandbox"
              element={
                <HeaderTitle title="Keap Sandbox">
                  <KeapSandbox />
                </HeaderTitle>
              }
            />
          )}
        </Route>
        <Route
          path="/pending-links"
          element={
            hasOrdersLinkAccess ? (
              <HeaderTitle title="Pending Purchase Links">
                <PendingLinks />
              </HeaderTitle>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/completed-links"
          element={
            hasOrdersLinkAccess ? (
              <HeaderTitle title="Completed Purchase Links">
                <CompletedLinks />
              </HeaderTitle>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {hasPurchaseLinksDashboardAccess && (
          <Route
            path="/purchase-links-dashboard"
            element={
              <HeaderTitle title="Purchase Links Dashboard">
                <PurchaseLinksDashboard />
              </HeaderTitle>
            }
          />
        )}
        {hasPaymentErrorsDashboardAccess && (
          <Route
            path="/payment-errors-dashboard"
            element={
              <HeaderTitle title="Payment Errors Dashboard">
                <PaymentErrorsDashboard />
              </HeaderTitle>
            }
          />
        )}
        <Route
          path="/orders"
          element={
            hasOrdersLinkAccess ? (
              <HeaderTitle title="Orders">
                <Orders />
              </HeaderTitle>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {hasCouponConfigAccess && (
          <Route
            path="/coupons"
            element={
              <HeaderTitle title="Coupons Configuration">
                <Coupons />
              </HeaderTitle>
            }
          />
        )}
        {hasCouponConfigAccess && (
          <Route
            path="/coupons/new"
            element={
              <HeaderTitle title="New Coupon">
                <NewCoupon />
              </HeaderTitle>
            }
          />
        )}
        <Route
          path="/feature-summary"
          element={
            <HeaderTitle title="Feature Summary">
              <FeaturesTable />
            </HeaderTitle>
          }
        />
        {/*
        {Business_Scan_Access && (
          <Route
            path="/scan-form"
            element={
              <HeaderTitle title="Business Scan">
                <BusinessScan />
              </HeaderTitle>
            }
          />
        )}
        */}
        <Route
          path="/package-recommendation"
          element={
            <HeaderTitle title="Package Recommendation">
              <PackageRecommendation />
            </HeaderTitle>
          }
        />
        <Route
          path="/package-result/:selectedPackage"
          element={
            <HeaderTitle title="Package Result">
              <PackageResult />
            </HeaderTitle>
          }
        />
        {/*
        {Business_Scan_Access && (
          <Route
            path="/scan-form/:recordId"
            element={
              <HeaderTitle title="Business Scan">
                <BusinessScan />
              </HeaderTitle>
            }
          />
        )}
        {Business_Scan_Access && (
          <Route
            path="/scan-results/:recordId"
            element={
              <HeaderTitle title="Business Results">
                <BusinessResults />
              </HeaderTitle>
            }
          />
        )}
        {Business_Scan_Access && (
          <Route
            path="/scans"
            element={
              <HeaderTitle title="Business Scans">
                <BusinessScans />
              </HeaderTitle>
            }
          />
        )}
        */}
        <Route
          path="/demo-requests"
          element={
            <HeaderTitle title="Demo Requests">
              <AppointmentsRedesign />
            </HeaderTitle>
          }
        />
        <Route
          path="/price-calculator"
          element={
            <HeaderTitle title="Price Calculator">
              <PriceCalculator />
            </HeaderTitle>
          }
        />
        <Route
          path="/my-client-center"
          element={
            <HeaderTitle title="Client Center">
              <ControlCenterAdmin />
            </HeaderTitle>
          }
        />
        <Route
          path="/appointments"
          element={
            <HeaderTitle title="Appointments">
              <Appointments />
            </HeaderTitle>
          }
        />
        {/* <Route
          path="/productshowcase"
          element={
            <HeaderTitle title="Product Showcase">
              <ProductShowcase />
            </HeaderTitle>
          }
        /> */}
        <Route
          path="/my-accounts"
          element={
            <HeaderTitle title="My Accounts">
              <MyAccounts />
            </HeaderTitle>
          }
        />
        <Route
          path="/all-accounts"
          element={
            <HeaderTitle title="All Accounts">
              <AllAccounts />
            </HeaderTitle>
          }
        />
        <Route
          path="/tools"
          element={
            <HeaderTitle title="Collateral & Education">
              <ToolsPage />
            </HeaderTitle>
          }
        >
          <Route
            index
            element={<Navigate to={TOOL_SECTIONS[0].path} replace />}
          />
          {TOOL_SECTIONS.map(s => (
            <Route
              key={s.id}
              path={s.id}
              element={<ToolsContent section={s.id} />}
            />
          ))}
        </Route>
        <Route
          path="/new-demo-request"
          element={
            <HeaderTitle title="New Demo Request">
              <NewDemo />
            </HeaderTitle>
          }
        />
        <Route
          path="/"
          element={
            <HeaderTitle title="Accounts">
              <Accounts />
            </HeaderTitle>
          }
        />
        <Route
          path="/order/new"
          element={
            <HeaderTitle title="New Purchase Link">
              <NewOrder />
            </HeaderTitle>
          }
        />
        <Route
          path="/order/upgrade"
          element={
            <HeaderTitle title="Upgrade Order">
              <UpgradeOrder />
            </HeaderTitle>
          }
        />
        <Route
          path="/new-order"
          element={
            <HeaderTitle title="New Order">
              <NewOrderShortCut />
            </HeaderTitle>
          }
        />
        <Route
          path="/success"
          element={
            <HeaderTitle title="Success">
              <Success />
            </HeaderTitle>
          }
        />
        <Route
          path="/support"
          element={
            <HeaderTitle title="Support">
              <Support />
            </HeaderTitle>
          }
        />
        {featureFlags.Utility_Page && (
          <Route
            path="/admin"
            element={
              <HeaderTitle title="Admin">
                <Admin />
              </HeaderTitle>
            }
          />
        )}
        <Route
          path="*"
          element={
            <HeaderTitle title="404 - Not Found">
              <NotFoundPage />
            </HeaderTitle>
          }
        />
        <Route
          path="/reports/bundle-coupon"
          element={
            <HeaderTitle title="Coupon Report">
              <BundleCouponReport />
            </HeaderTitle>
          }
        />
        {hasProposalMetricsAccess && (
          <Route
            path="/reports/proposal-metrics"
            element={
              <HeaderTitle title="Proposal Metrics">
                <ProposalMetrics />
              </HeaderTitle>
            }
          />
        )}
        <Route path="/sales-collateral" element={<SalesCollateral />} />
        <Route path="/check-status" element={<CheckStatus />} />
        <Route
          path="/manage-product-rules"
          element={<ProductGrpServiceIdManager />}
        />
        {hasFeatureFlagsReadAccess && (
          <Route
            path="/features"
            element={
              <HeaderTitle title="Feature Flags">
                <FeatureFlags />
              </HeaderTitle>
            }
          />
        )}

        <Route path="/proposal-tool" element={<ProposalMain />}>
          {/* Default /proposal dashboard route */}
          <Route
            index
            element={<Navigate to="/proposal-tool/dashboard" replace />}
          />

          {/* Child Routes */}
          <Route
            path="dashboard"
            element={
              <HeaderTitle title="Dashboard">
                <ProposalToolDashboard />
              </HeaderTitle>
            }
          />
          <Route
            path="proposal-builder"
            element={
              <HeaderTitle title="Proposal Builder">
                <ProposalBuilder />
              </HeaderTitle>
            }
          />
        </Route>
      </Routes>
    </Sentry.ErrorBoundary>
  )
}

export default AuthenticatedApp
